import { CanvasEvents, FabricObject } from 'fabric';
import { Path } from 'opentype.js';
import { Polygon } from 'polygon-clipping';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import removeMediaElementOperation from 'editor/src/store/design/operation/removeMediaElementOperation';
import getSharedTextFieldCapsSelector from 'editor/src/store/design/selector/getSharedTextFieldCapsSelector';
import { Coords, ElementAddress, MediaText } from 'editor/src/store/design/types';
import { getOperativeCharCap } from 'editor/src/store/design/util/sharedTextFields';
import { matchesValueFormat } from 'editor/src/store/design/util/valueRegex';
import { setTextEditModeAction as setTextEditModeOperation } from 'editor/src/store/editor/slice';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import hasPersonalizationPluginEnabled from 'editor/src/store/plugins/selector/hasPersonalizationPluginEnabled';

import CustomFabricImage from 'editor/src/fabric/CustomFabricImage';
import FabricPathText from 'editor/src/fabric/FabricPathText';
import parseSvgPathToCommands from 'editor/src/util/parseSvgPathToCommands';
import useBrowserColor from 'editor/src/util/useBrowserColor';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';
import useMediaElementLiveUpdates from 'editor/src/util/useMediaElementLiveUpdates';

import { currentOperationManager } from 'editor/src/component/EditorArea/ElementOperationOverlay';
import FabricImageComponent from 'editor/src/component/EditorArea/fabricComponents/FabricImageComponent';
import FabricPathComponent from 'editor/src/component/EditorArea/fabricComponents/FabricPathComponent';
import FabricTextInputComponent from 'editor/src/component/EditorArea/fabricComponents/FabricTextInputComponent';
import { setClipPath } from 'editor/src/component/EditorArea/fabricComponents/fabricUtils';
import useSnapMatch from 'editor/src/component/EditorArea/snapping/useSnapMatch';
import ElementLoader from 'editor/src/component/EditorArea/Spread/Page/MediaElement/ElementLoader';
import fabricElementToObjectRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/fabricElementToObjectRect';
import getElementRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getElementRect';
import useHoverBox from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useHoverBox';
import useIsInteractable from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useIsInteractable';
import useMultiSelection from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useMultiSelection';
import useStoreSelection from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useStoreSelection';
import WarningIcon, {
  WarningIconInterface,
} from 'editor/src/component/EditorArea/Spread/Page/MediaElement/WarningIcon';
import zIndex from 'editor/src/component/EditorArea/Spread/zIndex';
import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useElementWarnings from 'editor/src/component/TopMenuDesktop/buttons/ButtonWarnings/useElementWarnings';

import fabricPropsToTextElementData from './fabricPropsToTextElementData';
import getDigitizedTextPosition from './getDigitizedTextPosition';
import getSvgOverlayOffset from './getSvgOverlayOffset';
import getTextClipPath from './getTextClipPath';
import getTextShadow from './getTextShadow';
import getWarningFrameColor from './getWarningFrameColor';
import isEmptyTextEligibleForRemoval from './isEmptyTextEligibleForRemoval';
import textElementDataToFabricProps from './textElementDataToFabricProps';
import { getFlexbileTextMaxWidth } from './updateTextElementWithoutRender';
import useDigitizedText from './useDigitizedText';
import useFontLoader from './useFontLoader';
import useProtectedFontRender from './useProtectedFontRender';
import useTextEditMode from './useTextEditMode';
import useTextUpdates from './useTextUpdates';

// Custom fonts (especially script/handwriting) can have extreme swashes that extend
// far beyond the element bounds. This padding value is passed to getTextClipPath
// so the clip rect is large enough that only the page boundary clips the text.
// Based on max font size 240pt with swashes up to ~4× font size.
// Performance: only affects polygon intersection calculation (two rectangles), negligible cost.
const CUSTOM_FONT_CLIP_PADDING = 1000;

// Matches $red in the personalization panel's own SCSS, so a mismatch reads
// as the same red whether the author is looking at the canvas or the panel.
const VALUE_FORMAT_MISMATCH_COLOR = '#F04A47';
const VALUE_FORMAT_MISMATCH_DASH = [4, 4];

// GAPI-61892: lines that overflow a fixed frame still print (the renderer does not clip
// text), so no clip of the canvas text may hide them either. 0 for every other text.
function getFixedFrameOverflow(fabricText: FabricPathText | null): number {
  return fabricText?.fixedFrame ? fabricText.getOverflowHeight() : 0;
}

function getClipPadding(fontFamily: string, fontSize: number): number {
  return fontFamily.startsWith('custom-') ? CUSTOM_FONT_CLIP_PADDING : fontSize;
}

interface Props {
  elementData: MediaText;
  elementAddress: ElementAddress;
  pageCoords: Coords;
  areaWidth: number;
  canvasRotation: CanvasRotation;
  contentClipPolygons: Polygon[];
  selected: boolean;
  isMobile: boolean;
  ignorePersonalizationLock: boolean;
  showGuides: boolean;
  contentClipPath: FabricObject | undefined;
}

function Text({
  elementData,
  elementAddress,
  pageCoords,
  areaWidth,
  canvasRotation,
  contentClipPolygons,
  selected,
  isMobile,
  ignorePersonalizationLock,
  showGuides,
  contentClipPath,
}: Props) {
  const { mm2px, px2mm } = useFabricUtils();
  const fabricCanvas = useFabricCanvas();
  const fabricElementRef = useRef<FabricPathText>(null);
  const digitizedImageRef = useRef<CustomFabricImage>(null);
  const warningRef = useRef<WarningIconInterface>(null);
  const dispatch = useDispatch();
  const [digiImageLoaded, setDigiImageLoaded] = useState(false);

  const { liveElement: element, liveUpdate } = useMediaElementLiveUpdates(elementData);

  const personalizationPluginEnabled = useSelector(hasPersonalizationPluginEnabled);
  const showEmptyPersonalizationFieldOutline = useSelector(
    (state) => state.hostSettings.showEmptyPersonalizationFieldOutline,
  );
  // The group's strictest cap for a member the value reaches, this layer's own
  // otherwise. Typing past the group's on canvas propagates the overrun to
  // every member, so the buyer meets an over-limit error on a value they never
  // typed on the page showing it — the same failure the join truncation exists
  // to prevent, arriving from the canvas. Every consumer of a cap in this
  // feature resolves it through getOperativeCharCap.
  const charCap = useSelector((state) => getOperativeCharCap(element, getSharedTextFieldCapsSelector(state)));
  const isInteractable = useIsInteractable(element, ignorePersonalizationLock);

  const { loadedFontFamily, isFontLoaded } = useFontLoader(element.extra.fontFamily);
  const textEditMode = useTextEditMode(fabricElementRef, selected);
  const { digitizedAsset, showDigitizedAsset, getFabricOptionsOnImageUpdate } = useDigitizedText(
    element,
    textEditMode.enabled,
    px2mm,
  );
  useProtectedFontRender(element, elementAddress, textEditMode.enabled);

  // Overlay that renders element.path (the server-supplied glyph outline) on the canvas.
  // Custom (protected) fonts render the path as a VECTOR (FabricPathComponent) — no
  // rasterization, so no intrinsic-pixel rounding (the right-edge clip) and no per-element
  // bitmap. Standard SSR fonts keep the SVG-image path (element dimensions, original behavior).
  const isCustomFont = element.extra.fontFamily.startsWith('custom-');

  const svgOverlayBbox = useMemo(() => {
    if (!element.path || textEditMode.enabled || !isCustomFont) {
      return undefined;
    }
    const commands = parseSvgPathToCommands(element.path);
    if (commands.length === 0) {
      return undefined;
    }
    const bboxPath = new Path();
    bboxPath.commands = commands;
    return bboxPath.getBoundingBox();
  }, [element.path, textEditMode.enabled, isCustomFont]);

  // Custom (protected) fonts are drawn by the vector overlay below; only standard SSR
  // fonts use the rasterized SVG-image overlay (original behavior).
  const svgPathOverlayUrl = useMemo(() => {
    if (!element.path || textEditMode.enabled || isCustomFont) {
      return undefined;
    }
    const vb = `0 0 ${element.width} ${element.height}`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" width="${element.width}" height="${element.height}"><path d="${element.path}" fill="${element.fill}"/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [element.path, element.fill, element.width, element.height, textEditMode.enabled, isCustomFont]);

  // Scale the SSR-font image so it fits the element dimensions (original behavior).
  const getSvgOverlayFabricOptions = useCallback(
    (image: CustomFabricImage) => {
      const img = image.getElement();
      if (!img.width || !img.height || !element.width || !element.height) {
        return {};
      }
      const scaleX = element.width / px2mm(img.width);
      const scaleY = element.height / px2mm(img.height);
      return { scaleX, scaleY };
    },
    [element.width, element.height, px2mm],
  );

  // Custom-font glyph path is rendered as a vector. The path is in element-local mm, so
  // sx/sy scale the glyph and mm2px(1) converts to canvas pixels (1mm -> mm2px(1)px, the
  // same convention as the diecut/line vector paths). A vector has no intrinsic raster
  // size, so there is no rounding loss and no clipping regardless of sx/sy.
  const showGlyphPathOverlay = !!svgOverlayBbox && !!element.path;
  const glyphOverlayScaleX = (element.sx ?? 1) * mm2px(1);
  const glyphOverlayScaleY = (element.sy ?? 1) * mm2px(1);

  // The base text element is hidden (opacity 0) whenever a substitute is drawn on top:
  //  - embroidery   -> digitized image of the text
  //  - SSR fonts    -> rasterized glyph image (svgPathOverlayUrl)
  //  - custom fonts -> vector glyph path (showGlyphPathOverlay)
  const isReplacedByOverlay =
    (showDigitizedAsset && !!digitizedAsset?.url && digiImageLoaded) || !!svgPathOverlayUrl || showGlyphPathOverlay;

  const textUpdates = useTextUpdates(
    pageCoords,
    element,
    elementAddress,
    fabricElementRef,
    canvasRotation,
    loadedFontFamily,
  );
  useStoreSelection(fabricElementRef, element.uuid, element.type, selected);

  const fabricProps = textElementDataToFabricProps(
    element,
    elementAddress.elementIndex,
    mm2px,
    isInteractable,
    isMobile,
    loadedFontFamily,
  );

  fabricProps.fill = useBrowserColor(fabricProps.fill);
  fabricProps.stroke = useBrowserColor(fabricProps.stroke);

  // a text with a warning (one that does not fit its fixed frame, say) gets the same coloured
  // frame as an image with a low resolution, so the warning reads the same on every element
  const { warnings: elementWarnings } = useElementWarnings(element);
  const warningFrameColor = getWarningFrameColor(elementWarnings);
  if (warningFrameColor) {
    fabricProps.borderColor = warningFrameColor;
    fabricProps.editingBorderColor = warningFrameColor;
    fabricProps.cornerColor = warningFrameColor;
    fabricProps.cornerStrokeColor = warningFrameColor;
  }

  const elementRect = useMemo(
    () => getElementRect(element, pageCoords, canvasRotation, mm2px),
    [element.x, element.y, element.r, element.width, element.height, pageCoords, canvasRotation, mm2px],
  );

  const svgOverlayOffset = useMemo(
    () =>
      getSvgOverlayOffset({
        bbox: svgOverlayBbox,
        sx: element.sx ?? 1,
        sy: element.sy ?? 1,
        angleDeg: elementRect.angle || 0,
        mm2px,
      }),
    [svgOverlayBbox, element.sx, element.sy, elementRect.angle, mm2px],
  );

  const { selectableFrameRect, activeSelectionOffset } = useMultiSelection(elementData.uuid, elementRect);

  const shadow = useMemo(
    () => getTextShadow(element.extra.shadow, element.extra.fontSize, mm2px),
    [element.extra.shadow, element.extra.fontSize, mm2px],
  );

  const outlineEmptyPersonalizationField =
    showEmptyPersonalizationFieldOutline &&
    personalizationPluginEnabled &&
    !element.personalizationLocked &&
    !element.extra.text.trim();

  const hoverBox = useHoverBox(
    elementRect,
    isMobile,
    selected || !isInteractable,
    canvasRotation,
    warningFrameColor,
    outlineEmptyPersonalizationField,
  );

  // A regex forced-format mismatch, drawn on the canvas the same way the
  // empty-field outline above is — an always-visible box rather than a hover
  // one, since the point is to be seen without the buyer hovering over it.
  // Not shown while the buyer is actually editing the text: every half-typed
  // value fails its own pattern, and flagging that mid-keystroke would just be
  // noise on top of what the buyer is already looking at.
  //
  // Checked directly on this element rather than through
  // getValueFormatMismatches (the full editor's shared selector): that
  // selector also walks hidden rows and shared-field groups across the whole
  // design to decide whether a text counts as offered at all, which this
  // component does not otherwise need. `hidden`/`personalizationLocked`/
  // `sample`/`variableColumn` are repeated here so a collapsed row, a locked
  // layer, the template's own placeholder, or a code-set sample does not draw
  // an outline nobody offered a field to fix.
  const hasValueFormatMismatch =
    !textEditMode.enabled &&
    !element.hidden &&
    !element.personalizationLocked &&
    !element.sample &&
    !element.extra.variableColumn &&
    !matchesValueFormat(element.extra.text, element.extra.valueFormat);

  const valueFormatOutline = useHoverBox(
    elementRect,
    isMobile,
    !hasValueFormatMismatch,
    canvasRotation,
    VALUE_FORMAT_MISMATCH_COLOR,
    true,
    VALUE_FORMAT_MISMATCH_DASH,
  );
  const isMouseDown = useRef(false);

  const customEvents = useMemo(
    () => ({
      'object:enter': () => {
        fabricElementRef.current?.enterEditing();
        fabricElementRef.current?.selectAll();
      },
      'object:escape': () => {
        if (!fabricElementRef.current?.isEditing) {
          // fabric already exits editing on escape
          fabricCanvas.discardActiveObject();
        }
      },
      fontFallbackLoaded: () => {
        fabricCanvas.requestRenderAll();
        textUpdates.onObjectModified();
      },
    }),
    [textUpdates.onObjectModified],
  );

  const onObjectManipulation = useCallback(() => {
    if (textEditMode.enabled) {
      dispatch(setTextEditModeOperation(false));
    }

    if (fabricElementRef.current) {
      if (textEditMode.enabled) {
        setClipPath(fabricElementRef.current, undefined);
      } else {
        const rect = fabricElementToObjectRect(fabricElementRef.current, undefined, false);
        rect.height += getFixedFrameOverflow(fabricElementRef.current);
        setClipPath(
          fabricElementRef.current,
          getTextClipPath(
            rect,
            getClipPadding(element.extra.fontFamily, fabricElementRef.current.fontSize ?? 0),
            contentClipPolygons,
            contentClipPath,
          ),
        );
      }
    }
  }, [contentClipPolygons, textEditMode.enabled, contentClipPath, element.extra.curve]);

  const onExitEditing = useCallback(() => {
    const text = fabricElementRef.current?.text;

    // Check if element is empty on focus out and if it is delete it.
    // A personalizable text layer (personalization plugin on + toggle switched on,
    // i.e. not personalizationLocked) is exempt: it must survive being emptied so the
    // placeholder box stays as a personalization layer instead of being deleted.
    const isPersonalizableLayer = personalizationPluginEnabled && !element.personalizationLocked;
    const textEligibleForRemove = isEmptyTextEligibleForRemoval(
      text,
      isMouseDown.current,
      isPersonalizableLayer,
      !!element.extra.textFlow,
    );
    if (textEligibleForRemove) {
      dispatch(removeMediaElementOperation(elementAddress));
    } else {
      // Typing already queues the measured text update. Flush it before leaving
      // edit mode, but do not measure again: focusing unchanged custom text must
      // preserve its imported frame/path and must not create an undo entry.
      textUpdates.flushTextUpdates();
    }

    textEditMode.onExitEditing();
  }, [
    textEditMode.onExitEditing,
    textUpdates,
    elementAddress,
    element.personalizationLocked,
    element.extra.textFlow,
    personalizationPluginEnabled,
  ]);

  const clipPath = useMemo(
    () =>
      getTextClipPath(
        { ...elementRect, height: elementRect.height + getFixedFrameOverflow(fabricElementRef.current) },
        getClipPadding(element.extra.fontFamily, element.extra.fontSize),
        contentClipPolygons,
        contentClipPath,
      ),
    [
      element.extra.fontSize,
      element.extra.fontFamily,
      elementRect,
      contentClipPolygons,
      contentClipPath,
      element.extra.curve,
      element.extra.fixedFrame ? element.extra : undefined,
    ],
  );

  const snapMatch = useSnapMatch(element.uuid, showGuides && !element.locked, pageCoords);

  const onMouseDown = useCallback(
    (e: CanvasEvents['mouse:down']) => {
      if (isMobile && element.sample) {
        textEditMode.onEnterEditing();
      }
      isMouseDown.current = true;
      snapMatch.onMouseDown(e);
    },
    [
      snapMatch.onMouseDown,
      isMobile,
      element.sample,
      element.uuid,
      element.name,
      element.extra.text,
      element.width,
      element.height,
      element.extra.fontFamily,
      element.extra.flexibleWidth,
      element.path,
      selected,
      textEditMode.enabled,
    ],
  );

  const onMouseMove = useCallback(
    (e: CanvasEvents['mouse:move']) => {
      if (
        !isMouseDown.current ||
        fabricCanvas.getActiveObject() !== fabricElementRef.current ||
        !fabricElementRef?.current?.oCoords
      ) {
        return;
      }
      snapMatch.onMouseMove(e);
      onObjectManipulation();

      if (fabricElementRef.current) {
        const newElement = {
          ...element,
          ...fabricPropsToTextElementData(px2mm, pageCoords, fabricElementRef.current, canvasRotation),
        };
        liveUpdate(newElement);
        let isElementMove = false;
        if (fabricElementRef.current.oCoords && e.transform) {
          fabricElementRef.current.setCoords();
          isElementMove = e.transform.action === 'drag';
          currentOperationManager.emit(
            'objectUpdating',
            newElement,
            fabricElementRef.current.oCoords,
            isElementMove ? 'move' : 'resize',
          );
        }

        if (digitizedImageRef.current) {
          const { left, top, angle } = fabricElementRef.current;
          const { scaleX, scaleY } = digitizedImageRef.current;
          if (!Number.isNaN(left) && !Number.isNaN(top) && scaleX && scaleY) {
            digitizedImageRef.current.set(
              getDigitizedTextPosition({
                originalLeft: left as number,
                originalTop: top as number,
                scaleX,
                scaleY,
                angle: angle || 0,
                margin: digitizedAsset?.margin || 0,
              }),
            );
          }

          digitizedImageRef.current.set({
            clipPath: fabricElementRef.current.clipPath,
            opacity: digitizedImageRef.current.opacity && isElementMove ? 1 : 0,
          });

          if (fabricElementRef.current.opacity === 0 && !isElementMove) {
            fabricElementRef.current.set('opacity', 1);
          }
        }
      }
    },
    [snapMatch.onMouseMove, px2mm, pageCoords, canvasRotation, onObjectManipulation, digitizedAsset?.margin],
  );

  const onMouseUp = useCallback(() => {
    isMouseDown.current = false;
    snapMatch.onMouseUp();
    currentOperationManager.emit('objectUpdateStop');
  }, [snapMatch.onMouseUp]);

  useEffect(() => {
    // correctly set the clippath after live updates of the element because the height may have changed
    if (fabricElementRef.current) {
      if (textEditMode.enabled) {
        setClipPath(fabricElementRef.current, undefined);
      } else {
        const rect = fabricElementToObjectRect(fabricElementRef.current, undefined, false);
        rect.left -= activeSelectionOffset.left;
        rect.top -= activeSelectionOffset.top;
        rect.height += getFixedFrameOverflow(fabricElementRef.current);
        setClipPath(
          fabricElementRef.current,
          getTextClipPath(
            rect,
            getClipPadding(element.extra.fontFamily, fabricElementRef.current.fontSize ?? 0),
            contentClipPolygons,
            contentClipPath,
          ),
        );
      }
    }
    // a fixed frame keeps its rect while the text inside it changes, so the overflow
    // it has to leave room for is only known from the text itself
  }, [elementRect, activeSelectionOffset, element.extra.fixedFrame ? element.extra : undefined]);

  const maxWidth = getFlexbileTextMaxWidth(
    elementRect.width,
    elementRect.left,
    element.extra.textAlign,
    pageCoords.left,
    areaWidth,
  );

  useLayoutEffect(() => {
    setDigiImageLoaded(false);
  }, [element.digitizing_hash]);

  const onDigiImageLoaded = useCallback(() => {
    setDigiImageLoaded(true);
  }, []);

  if (!isFontLoaded) {
    return null;
  }

  return (
    <>
      <FabricTextInputComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...fabricProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...selectableFrameRect}
        isFontLoaded={isFontLoaded}
        clipPath={textEditMode.enabled ? undefined : clipPath}
        customEvents={customEvents}
        hoverCursor={selected ? 'move' : 'pointer'}
        borderOpacityWhenMoving={0.4}
        uuid={element.uuid}
        ref={fabricElementRef}
        onMouseDown={textEditMode.enabled ? undefined : onMouseDown}
        onMouseMove={textEditMode.enabled ? undefined : onMouseMove}
        onMouseUp={onMouseUp}
        lockMovementX={textEditMode.enabled || fabricProps.lockMovementX}
        lockMovementY={textEditMode.enabled || fabricProps.lockMovementY}
        onMouseOver={hoverBox.onMouseOver}
        onMouseOut={hoverBox.onMouseOut}
        onModified={textUpdates.onObjectModified}
        onChanged={textUpdates.onTextChanged}
        onEditingEntered={textEditMode.onEnterEditing}
        onEditingExited={onExitEditing}
        opacity={element.hidden || isReplacedByOverlay ? 0 : 1}
        shadow={shadow}
        maxCharLimit={charCap}
        paintFirst="stroke"
        maxWidth={maxWidth}
      />
      {digitizedAsset?.url && !textEditMode.enabled && showDigitizedAsset && (
        <FabricImageComponent
          ref={digitizedImageRef}
          source={digitizedAsset.url}
          left={elementRect.left}
          top={elementRect.top}
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={textEditMode.enabled ? undefined : clipPath}
          opacity={element.hidden ? 0 : 1}
          uuid={element.uuid}
          getFabricOptionsOnUpdate={getFabricOptionsOnImageUpdate}
          angle={element.r}
          crossOrigin="anonymous"
          onImageLoaded={onDigiImageLoaded}
        />
      )}
      {svgPathOverlayUrl && (
        <FabricImageComponent
          source={svgPathOverlayUrl}
          left={elementRect.left + svgOverlayOffset.x}
          top={elementRect.top + svgOverlayOffset.y}
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={clipPath}
          opacity={element.hidden ? 0 : 1}
          uuid={element.uuid}
          getFabricOptionsOnUpdate={getSvgOverlayFabricOptions}
          angle={element.r}
          crossOrigin="anonymous"
        />
      )}
      {showGlyphPathOverlay && element.path && (
        <FabricPathComponent
          elementPath={element.path}
          left={elementRect.left + svgOverlayOffset.x}
          top={elementRect.top + svgOverlayOffset.y}
          scaleX={glyphOverlayScaleX}
          scaleY={glyphOverlayScaleY}
          fill={element.fill}
          stroke=""
          strokeWidth={0}
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={clipPath}
          opacity={element.hidden ? 0 : 1}
          angle={element.r}
        />
      )}
      {(element.digitizing || (showDigitizedAsset && !digitizedAsset?.url && elementData.stitch_count !== -1)) && (
        <ElementLoader frameRect={elementRect} element={element} />
      )}
      {hoverBox.render()}
      {valueFormatOutline.render()}
      <WarningIcon
        ref={warningRef}
        frameRect={elementRect}
        zIndex={zIndex.WARNING_WATERMARK}
        element={element}
        forceHighWarning={hasValueFormatMismatch}
      />
    </>
  );
}

export default React.memo(Text);
